import React from 'react';
import { 
  Smartphone, 
  Building2, 
  FileText, 
  CheckCircle,
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
  Bot,
  Globe,
  ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';

const SolutionsDigitalesSection = () => {

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="solutions" className="py-32 bg-gradient-to-b from-black via-[#0a0a0a] to-black relative overflow-hidden" data-testid="solutions-section">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A55C]/30 to-transparent" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#C9A55C]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#C9A55C]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.3em] uppercase text-[#C9A55C] mb-4">
            <Sparkles className="w-4 h-4" />
            Solutions Digitales
            <Sparkles className="w-4 h-4" />
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Outils IA & <span className="text-[#C9A55C]">Automatisations</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Agents IA, workflows automatisés, générateurs de documents. Des solutions prêtes à l'emploi.
          </p>
        </div>

        {/* ==================== CATÉGORIE 1 : AGENTS IA ==================== */}
        <div className="mb-16">
          <h3 className="text-2xl font-serif font-bold text-[#C9A55C] mb-8 flex items-center gap-3">
            <FileText className="w-6 h-6" />
            Agents IA
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* AGENT IA ADMINISTRATIF */}
            <div className="bg-black border-2 border-[#C9A55C]/30 p-8 lg:p-10 relative overflow-hidden group hover:border-[#C9A55C]/60 transition-all duration-500">
              <div className="absolute top-0 right-0 bg-[#C9A55C] text-black px-4 py-1 text-xs font-bold tracking-wider">
                AGENT IA
              </div>
              <div className="w-16 h-16 border-2 border-[#C9A55C] flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-[#C9A55C]" />
              </div>
              <h3 className="text-3xl font-serif font-bold text-white mb-2">
                Agent IA <span className="text-[#C9A55C]">Administratif</span>
              </h3>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-1 text-xs text-green-400 mb-6">
                <CheckCircle className="w-3 h-3" />
                Disponible
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                Déposez vos courriers. L'IA rédige vos réponses administratives automatiquement.
              </p>
              <p className="text-white/70 mb-4 leading-relaxed text-sm">
                CAF, CPAM, Impôts, Employeur, Assurance, Banque, Logement, Santé, Justice, Contrats, Réclamations.
              </p>
              <p className="text-white/70 mb-6 leading-relaxed text-sm font-semibold text-[#C9A55C]">
                Réponses professionnelles, prêtes à envoyer. Sans limite de domaine.
              </p>
              <a
                href="https://zerux.helpadn.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-gold rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                data-testid="agent-ia-cta"
              >
                <ExternalLink className="w-4 h-4" />
                Essayer la démo
              </a>
            </div>

            {/* AGENT IA ORGANISATRICE FINANCIÈRE */}
            <div className="bg-black border-2 border-[#C9A55C]/30 p-8 lg:p-10 relative overflow-hidden group hover:border-[#C9A55C]/60 transition-all duration-500">
              <div className="absolute top-0 right-0 bg-[#C9A55C] text-black px-4 py-1 text-xs font-bold tracking-wider">
                AGENT IA
              </div>
              <div className="w-16 h-16 border-2 border-[#C9A55C] flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-[#C9A55C]" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-2">
                Agent IA <span className="text-[#C9A55C]">Organisatrice Financière</span>
              </h3>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-1 text-xs text-green-400 mb-6">
                <CheckCircle className="w-3 h-3" />
                Disponible
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                Suivi automatisé de vos obligations financières. Impôts, factures, échéances.
              </p>
              <p className="text-white/70 mb-6 leading-relaxed text-sm text-[#C9A55C] font-semibold">
                Plus aucune deadline oubliée.
              </p>
              <a
                href="https://smart-personal-ai-1.preview.emergentagent.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-gold rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                data-testid="organisatrice-ia-cta"
              >
                <ExternalLink className="w-4 h-4" />
                Essayer l'Agent IA
              </a>
            </div>

          </div>
        </div>

        {/* ==================== CATÉGORIE 2 : BOTS ==================== */}
        <div className="mb-16">
          <h3 className="text-2xl font-serif font-bold text-[#C9A55C] mb-8 flex items-center gap-3">
            <Bot className="w-6 h-6" />
            Bots
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* BOTS VIRTUELS */}
            <div className="bg-black border-2 border-[#C9A55C]/30 p-8 lg:p-10 relative overflow-hidden group hover:border-[#C9A55C]/60 transition-all duration-500">
              <div className="absolute top-0 right-0 bg-[#C9A55C] text-black px-4 py-1 text-xs font-bold tracking-wider">
                TOUS MÉTIERS
              </div>
              <div className="w-16 h-16 border-2 border-[#C9A55C] flex items-center justify-center mb-6">
                <Bot className="w-8 h-8 text-[#C9A55C]" />
              </div>
              <h3 className="text-3xl font-serif font-bold text-white mb-2">
                Chatbots <span className="text-[#C9A55C]">Professionnels</span>
              </h3>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-1 text-xs text-green-400 mb-6">
                <CheckCircle className="w-3 h-3" />
                Disponible
              </div>
              <p className="text-white/70 mb-4 leading-relaxed">
                Assistant IA intégré à votre site. Répond à vos clients, qualifie les demandes, prend les rendez-vous.
              </p>
              <div className="mb-6">
                <div className="space-y-2">
                  {['Restaurateurs', 'Artisans', 'Professions libérales', 'Commerces'].map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm text-white/80">
                      <CheckCircle className="w-3 h-3 text-[#C9A55C] flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
                <p className="text-[#C9A55C] font-semibold mt-4">Actif 24h/24. Ne perdez plus de clients.</p>
              </div>
              <div className="space-y-3">
                <a
                  href="https://nmik2vpa4iczi.kimi.page/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full btn-gold rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                  data-testid="bot-virtuel-cta"
                >
                  <ExternalLink className="w-4 h-4" />
                  Démo : Assistant Avocat
                </a>
                <a
                  href="https://3rusqpkphvufk.ok.kimi.link/?sharetype=link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full border border-[#C9A55C] text-[#C9A55C] hover:bg-[#C9A55C]/10 rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2 transition-colors"
                  data-testid="bot-virtuel-cta-3"
                >
                  <ExternalLink className="w-4 h-4" />
                  Démo : Bot Restaurateur
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* ==================== CATÉGORIE 3 : GUIDES INTERACTIFS ==================== */}
        <div className="mb-16">
          <h3 className="text-2xl font-serif font-bold text-[#C9A55C] mb-8 flex items-center gap-3">
            <Globe className="w-6 h-6" />
            Guides Interactifs
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* GUIDE MÉCANIQUE */}
            <div className="bg-black border-2 border-[#C9A55C]/30 p-8 relative overflow-hidden group hover:border-[#C9A55C]/60 transition-all duration-500">
              <div className="absolute top-0 right-0 bg-[#C9A55C] text-black px-4 py-1 text-xs font-bold tracking-wider">
                DÉMO
              </div>
              <div className="mb-6 rounded overflow-hidden">
                <video controls className="w-full">
                  <source src="https://customer-assets-lqy194kg.emergentagent.net/job_service-devis-1/artifacts/licahzmt_E96D3B2E-FBC1-4B9F-AD30-9D20AFA5B33F.mp4" type="video/mp4" />
                </video>
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-2">
                Guide <span className="text-[#C9A55C]">Mécanique</span>
              </h3>
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-3 py-1 text-xs text-blue-400 mb-4">
                Démonstration
              </div>
              <p className="text-white/70 mb-6 leading-relaxed text-sm">
                Guide interactif IA conçu sur mesure. Parcours utilisateur intelligent et automatisé.
              </p>
              <a
                href="https://guideintermecanohelpadn.netlify.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-gold rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                data-testid="guide-meca-demo-cta"
              >
                <ExternalLink className="w-4 h-4" />
                Voir la démo
              </a>
            </div>

          </div>
        </div>

        {/* ==================== CATÉGORIE 4 : DIAGNOSTICS ==================== */}
        <div className="mb-16">
          <h3 className="text-2xl font-serif font-bold text-[#C9A55C] mb-8 flex items-center gap-3">
            <Shield className="w-6 h-6" />
            Diagnostics
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* DIAGNOSTIC DÉMO */}
            <div className="bg-black border-2 border-[#C9A55C]/30 p-8 relative overflow-hidden group hover:border-[#C9A55C]/60 transition-all duration-500">
              <div className="absolute top-0 right-0 bg-[#C9A55C] text-black px-4 py-1 text-xs font-bold tracking-wider">
                DÉMO
              </div>
              <div className="mb-6 rounded overflow-hidden">
                <video controls className="w-full">
                  <source src="https://customer-assets-lqy194kg.emergentagent.net/job_service-devis-1/artifacts/6w6rosda_FD2B15B1-27A2-4D76-8357-911B5D7C7AC0.mp4" type="video/mp4" />
                </video>
              </div>
              <h3 className="text-2xl font-serif font-bold text-white mb-2">
                Diagnostic <span className="text-[#C9A55C]">Accessibilité</span>
              </h3>
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-3 py-1 text-xs text-blue-400 mb-4">
                Démonstration
              </div>
              <p className="text-white/70 mb-6 leading-relaxed text-sm">
                Outil de diagnostic IA pour l'accessibilité commerces. Analyse automatisée et rapport structuré.
              </p>
              <a
                href="/boutique"
                className="w-full btn-gold rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                data-testid="diagnostic-demo-cta"
              >
                <ExternalLink className="w-4 h-4" />
                En savoir plus
              </a>
            </div>

          </div>
        </div>

        {/* ==================== CATÉGORIE 5 : LOGICIELS ==================== */}
        <div className="mb-8">
          <h3 className="text-2xl font-serif font-bold text-[#C9A55C] mb-8 flex items-center gap-3">
            <Smartphone className="w-6 h-6" />
            Logiciels & Applications IA
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* EASY PACK ADMINISTRATIF PUBLIC */}
            <div className="bg-black border-2 border-[#C9A55C]/30 p-8 lg:p-10 relative overflow-hidden group hover:border-[#C9A55C]/60 transition-all duration-500">
              <div className="absolute top-0 right-0 bg-[#C9A55C] text-black px-4 py-1 text-xs font-bold tracking-wider">
                SERVICE PUBLIC
              </div>
              <div className="w-16 h-16 border-2 border-[#C9A55C] flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-[#C9A55C]" />
              </div>
              <h3 className="text-3xl font-serif font-bold text-white mb-2">
                Easy Pack <span className="text-[#C9A55C]">Administratif Public</span>
              </h3>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-1 text-xs text-green-400 mb-6">
                <CheckCircle className="w-3 h-3" />
                Démo disponible
              </div>
              <p className="text-white/70 mb-6 leading-relaxed">
                Solution IA conçue pour le <strong className="text-[#C9A55C]">service public local</strong>. Automatisation de l'accompagnement usagers.
              </p>
              <div className="mb-6">
                <p className="text-xs text-[#C9A55C] font-semibold tracking-wider uppercase mb-3">Structures concernées :</p>
                <div className="grid grid-cols-2 gap-2">
                  {['Mairies', 'CCAS', 'France Services', 'Centres sociaux', 'Missions locales', 'CHRS'].map((target) => (
                    <div key={target} className="flex items-center gap-2 text-sm text-white/80">
                      <CheckCircle className="w-3 h-3 text-[#C9A55C] flex-shrink-0" />
                      {target}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/50 mt-2">+ Intercommunalités, PIMMS, associations d'insertion...</p>
              </div>
              <a
                href="https://easy-pack-pro.preview.emergentagent.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn-gold rounded-none h-12 text-sm font-semibold tracking-wider uppercase flex items-center justify-center gap-2"
                data-testid="easypro-cta"
              >
                <ExternalLink className="w-4 h-4" />
                Voir la démo
              </a>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default SolutionsDigitalesSection;
